import React from "react";
import { splitIntoSentences } from "../lib/features";
import {
  Download,
  Printer,
  RotateCcw,
  Highlighter,
  MessageSquare,
  ThumbsUp,
  ThumbsDown,
  Wand2,
  BarChart2,
  ChevronUp,
  GraduationCap,
  RefreshCw,
  Activity,
  BookOpen,
  FileText,
  Repeat,
  Cpu
} from "lucide-react";

/**
 * Calculates a proportional CSS skeleton shimmer animation duration (in seconds)
 * based on the length of the pending text and the individual segment length.
 *
 * Longer segments and longer documents have proportionally slower shimmer cycles:
 * - Brief phrases / fragments (<35 chars): 1.6s - 2.0s (brisk, nimble cycle)
 * - Standard sentences (70 - 100 chars): 2.4s - 2.8s (natural scanning cadence)
 * - Extended sentences (150 - 250+ chars): 3.5s - 5.2s (slow, deliberate cycle)
 *
 * This ensures that the shimmer speed visually reflects the syntactic density and
 * sentence length variance (burstiness) of the document while it is loading.
 */
const getSegmentShimmerDuration = (
  segmentText: string,
  totalCharLength: number = 0
): number => {
  const charCount = segmentText.trim().length;
  if (charCount === 0) return 2.4;

  // Global document scale: slightly stretches shimmer cycle for longer texts (0.85x to 1.30x)
  const docScale = Math.min(1.30, Math.max(0.85, 0.85 + (totalCharLength / 2400) * 0.45));

  // Normalized ratio of this segment relative to a standard 80-character sentence
  // Bounds between 0.35 (short clause) and 3.0 (sprawling compound sentence)
  const segmentRatio = Math.min(3.0, Math.max(0.35, charCount / 80));

  // Power-curve scaling: longer segments get proportionally slower cycle periods
  const duration = 2.4 * docScale * Math.pow(segmentRatio, 0.55);

  // Return clean float clamped between 1.5s and 5.4s
  return Math.round(Math.min(5.4, Math.max(1.5, duration)) * 100) / 100;
};

/**
 * SkeletonBone renders a single placeholder shape (rectangle, pill, badge, circle)
 * with a continuous, smooth left-to-right shimmer wave.
 */
const SkeletonBone: React.FC<{
  className?: string;
  duration?: number;
  children?: React.ReactNode;
}> = ({ className = "", duration, children }) => (
  <div
    className={`relative overflow-hidden bg-slate-200/80 dark:bg-slate-800/80 ${className}`}
    style={duration ? ({ "--skeleton-wave-duration": `${duration}s` } as React.CSSProperties) : undefined}
  >
    <div className="skeleton-wave" />
    {children}
  </div>
);

interface ResultSkeletonLoaderProps {
  text?: string;
}

/**
 * ResultSkeletonLoader renders an exact 1:1 structural replica of the forensic results screen:
 * 1. Action Header (Title, Subtitle, Download PDF, Print, New Scan buttons)
 * 2. Consolidated Unified Diagnostic Verdict Banner (AccuracyGauge with SVG radial track, dynamic word/sentence stats)
 * 3. Model Accuracy Feedback (FeedbackCard with ThumbsUp/ThumbsDown prompt)
 * 4. Interactive Document Forensics (ExplainableText that OCCUPIES THE EXACT TYPOGRAPHIC SPACE OF THE TEXT)
 * 5. Stylometric Deep Dive Diagnostics Drawer (DashboardMetrics with full SVG Spider Radar + 6-card bento grid)
 * 6. Academic Verification Sign-off & Forensics Seal
 * 7. Next Step Actions Panel
 */
export const ResultSkeletonLoader: React.FC<ResultSkeletonLoaderProps> = ({ text = "" }) => {
  const [stage, setStage] = React.useState<number>(1);
  const [progress, setProgress] = React.useState<number>(25);

  React.useEffect(() => {
    const t1 = setTimeout(() => { setStage(2); setProgress(55); }, 220);
    const t2 = setTimeout(() => { setStage(3); setProgress(82); }, 580);
    const t3 = setTimeout(() => { setStage(4); setProgress(96); }, 980);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const trimmedText = text ? text.trim() : "";
  const words = trimmedText ? trimmedText.split(/\s+/) : [];
  const wordCount = words.length;

  // Segment the actual text using the unified sentence-boundary tokenizer
  const rawSentences = trimmedText.length > 0 ? splitIntoSentences(text) : [];

  const sentenceCount = rawSentences.length || (wordCount > 0 ? Math.ceil(wordCount / 18) : 0);

  // Document-wide wave duration based on pending text length (longer texts = proportionally slower ambient cycle)
  const documentWaveDuration = Math.round(
    Math.min(4.6, Math.max(2.0, 2.2 + (trimmedText.length / 1500) * 1.4)) * 100
  ) / 100;

  return (
    <div
      className="flex flex-col gap-6 w-full my-6 animate-fade-in"
      id="results-root-section"
      data-skeleton="true"
      style={{
        ["--skeleton-wave-duration" as any]: `${documentWaveDuration}s`,
      }}
      aria-busy="true"
      aria-label="Analyzing document and generating forensic report..."
    >
      {/* Zeigarnik Effect & Goal Gradient: Live Multi-Stage Progress Tracker */}
      <div className="rounded-2xl p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col gap-2.5">
        <div className="flex items-center justify-between text-xs flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-800 dark:text-slate-200 shrink-0" />
            <span className="font-bold text-slate-900 dark:text-white">
              {stage === 1 && "Step 1 of 4: Normalizing sentences and tokenizing structure..."}
              {stage === 2 && "Step 2 of 4: Computing lexical entropy, perplexity & burstiness..."}
              {stage === 3 && "Step 3 of 4: Generating sentence-by-sentence XAI attributions..."}
              {stage === 4 && "Step 4 of 4: Finalizing pedagogical integrity report..."}
            </span>
          </div>
          <div className="flex items-center gap-1.5 font-mono text-xs font-semibold text-slate-600 dark:text-slate-400">
            <span>{progress}%</span>
            <span className="text-[11px] text-slate-400">({stage < 4 ? "almost done" : "finalizing"})</span>
          </div>
        </div>

        {/* Accelerating Goal Gradient progress bar */}
        <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden relative">
          <div 
            className="h-full bg-slate-900 dark:bg-white rounded-full transition-all duration-300 ease-out shadow-xs"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Stepper dots (Zeigarnik visual proof of progressive completion) */}
        <div className="grid grid-cols-4 gap-2 pt-1 text-[11px]">
          {[
            { label: "Tokenize", step: 1 },
            { label: "Stylometrics", step: 2 },
            { label: "Sentence XAI", step: 3 },
            { label: "Report", step: 4 },
          ].map((item) => (
            <div key={item.step} className="flex items-center gap-1.5 min-w-0">
              <div className={`w-2 h-2 rounded-full shrink-0 transition-colors ${
                stage >= item.step ? "bg-slate-900 dark:bg-white" : "bg-slate-200 dark:bg-slate-700"
              }`} />
              <span className={`truncate text-[10px] ${
                stage >= item.step ? "font-semibold text-slate-800 dark:text-slate-200" : "text-slate-500 dark:text-slate-400"
              }`}>
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 1. Results Header Action Bar (Matches App.tsx Results Header) */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-slate-200/80 dark:border-slate-800/80 pt-4">
        <div className="space-y-1.5">
          <SkeletonBone className="h-6 w-56 sm:w-64 rounded-xl" />
          <SkeletonBone className="h-3.5 w-72 sm:w-80 rounded-md" />
        </div>

        <div className="flex items-center gap-2 flex-nowrap shrink-0 overflow-x-auto pb-1 sm:pb-0">
          <button
            type="button"
            disabled
            className="py-2 px-3.5 sm:px-4 rounded-xl bg-slate-900/80 dark:bg-white/80 text-white dark:text-slate-900 text-xs font-bold shadow-xs flex items-center gap-1.5 cursor-wait opacity-80 shrink-0 whitespace-nowrap"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Download PDF</span>
          </button>

          <button
            type="button"
            disabled
            className="py-2 px-3 sm:px-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 text-xs font-semibold text-slate-500 flex items-center gap-1.5 cursor-wait shrink-0 whitespace-nowrap"
          >
            <Printer className="w-3.5 h-3.5" />
            <span>Print</span>
          </button>

          <button
            type="button"
            disabled
            className="py-2 px-3 sm:px-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 text-xs font-semibold text-slate-500 flex items-center gap-1.5 cursor-wait shrink-0 whitespace-nowrap"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>New Scan</span>
          </button>
        </div>
      </div>

      {/* 2. Unified Verdict Banner (Matches AccuracyGauge layout 1:1) */}
      <div 
        className="rounded-2xl p-5 sm:p-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 relative overflow-hidden shadow-xs"
        id="unified-verdict-banner"
        data-loading="true"
        style={{
          ["--skeleton-wave-duration" as any]: `${documentWaveDuration}s`,
          ["--shimmer-duration" as any]: `${documentWaveDuration}s`,
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-1 bg-slate-200 dark:bg-slate-800" />
        <div 
          className="skeleton-wave" 
          style={{ animationDuration: `${documentWaveDuration}s` }} 
        />
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          
          {/* Column 1: Circular Radial Gauge */}
          <div className="md:col-span-4 lg:col-span-3 flex flex-col items-center justify-center text-center score-radial-gauge-col">
            <div 
              className="gauge-circle-wrapper relative w-36 h-36 mx-auto flex items-center justify-center shrink-0"
              style={{ width: "144px", height: "144px", position: "relative" }}
            >
              {/* Radial track ring */}
              <div 
                className="w-36 h-36 rounded-full border-[10px] border-slate-100 dark:border-slate-800 relative overflow-hidden flex items-center justify-center"
              >
                <div 
                  className="skeleton-wave" 
                  style={{ animationDuration: `${documentWaveDuration}s` }}
                />
              </div>

              {/* Inner score disc */}
              <div 
                className="gauge-disc-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center text-center bg-white dark:bg-slate-900 w-24 h-24 rounded-full border border-slate-200 dark:border-slate-800 shadow-xs"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "96px",
                  height: "96px",
                  zIndex: 25,
                }}
              >
                <SkeletonBone duration={documentWaveDuration} className="h-7 w-14 rounded-md" />
                <span className="text-[10px] font-sans font-semibold uppercase tracking-wider text-slate-400 mt-1 select-none">
                  AI Score
                </span>
              </div>
            </div>
          </div>

          {/* Column 2: Assessment Verdict & Explanation */}
          <div className="md:col-span-8 lg:col-span-9 flex flex-col gap-3">
            <div className="flex items-center gap-2.5 flex-wrap">
              <SkeletonBone duration={documentWaveDuration} className="h-6 w-32 rounded-full" />
              <span className="text-xs text-slate-400 font-medium">
                {wordCount > 0 ? `${wordCount} words • ${sentenceCount} sentences` : "Analyzing text..."}
              </span>
            </div>

            <div className="space-y-2">
              <SkeletonBone duration={documentWaveDuration} className="h-4 w-full rounded-md" />
              <SkeletonBone duration={documentWaveDuration} className="h-4 w-4/5 rounded-md" />
            </div>

            {/* 3 Clean Overview Tags */}
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <SkeletonBone duration={documentWaveDuration} className="h-7 w-28 rounded-md" />
              <SkeletonBone duration={documentWaveDuration} className="h-7 w-32 rounded-md" />
              <SkeletonBone duration={documentWaveDuration} className="h-7 w-36 rounded-md" />
            </div>
          </div>

        </div>
      </div>

      {/* 3. User Feedback & Model Calibration (Matches FeedbackCard 1:1) */}
      <div
        className="glass-panel rounded-2xl sm:rounded-3xl p-4 sm:p-5 border border-slate-200/80 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 shadow-xs flex flex-col gap-3.5 relative overflow-hidden"
        id="skeleton-feedback-section"
      >
        <div className="skeleton-wave" />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-2.5 border-b border-slate-100 dark:border-slate-800/80">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 shrink-0">
              <MessageSquare className="w-4 h-4" />
            </div>
            <div>
              <h4 className="font-display font-bold text-sm text-slate-900 dark:text-white">
                Model Accuracy Feedback
              </h4>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Help calibrate and retrain future language verification models by validating this detection.
              </p>
            </div>
          </div>
          <SkeletonBone className="h-7 w-28 rounded-lg shrink-0 self-start sm:self-auto" />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <SkeletonBone className="h-4 w-56 rounded-md" />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-xs font-bold text-slate-500 flex items-center gap-2 cursor-wait">
              <ThumbsUp className="w-3.5 h-3.5 text-slate-400" />
              <span>Correct Prediction</span>
            </div>
            <div className="px-3.5 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 text-xs font-bold text-slate-500 flex items-center gap-2 cursor-wait">
              <ThumbsDown className="w-3.5 h-3.5 text-slate-400" />
              <span>Incorrect Prediction</span>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Interactive Document Forensics Canvas (Matches ExplainableText 1:1 and OCCUPIES THE EXACT SPACE OF THE USER'S TEXT) */}
      <div
        className="glass-panel rounded-3xl p-5 sm:p-7 space-y-4 border border-slate-200/80 dark:border-slate-800/90 shadow-sm relative overflow-hidden"
        id="skeleton-text-forensics-canvas"
      >
        {/* Header with Title and Heatmap Legend */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-150/60 dark:border-slate-800/80 pb-4">
          <div>
            <h3 className="font-display font-bold text-slate-900 dark:text-white text-base sm:text-lg flex items-center gap-2">
              <Highlighter className="w-5 h-5 text-slate-700 dark:text-slate-300" />
              <span>Interactive Document Forensics</span>
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
              Analyzing sentence variance, perplexity spikes, and local token repetition distributions.
            </p>
          </div>

          {/* Filter Chips Bar (exact replica of ExplainableText filter tabs) */}
          <div className="flex items-center gap-1.5 overflow-x-auto max-w-full pb-1 sm:pb-0 sm:flex-wrap bg-slate-100 dark:bg-slate-800 p-1 rounded-xl text-xs font-semibold scrollbar-none">
            <span className="px-3 py-1.5 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white font-bold shadow-2xs flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-slate-900 dark:bg-white animate-pulse" />
              <span>All Sentences ({sentenceCount > 0 ? sentenceCount : "..."})</span>
            </span>
            <span className="px-3 py-1.5 rounded-lg text-slate-500 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-rose-400/60" />
              <span>Synthetic Flagged</span>
            </span>
            <span className="px-3 py-1.5 rounded-lg text-slate-500 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-400/60" />
              <span>Borderline Rhythm</span>
            </span>
            <span className="px-3 py-1.5 rounded-lg text-slate-500 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400/60" />
              <span>Human Cadence</span>
            </span>
          </div>
        </div>

        {/* 
          CRITICAL: Interactive Document Reading Canvas with EXACT TYPOGRAPHIC SPACE OCCUPATION.
          Instead of arbitrary boxes, each sentence pill renders in-situ using the identical font, line-height,
          wrapping, and scroll container as ExplainableText!
        */}
        <div
          className="prose prose-slate dark:prose-invert max-w-none text-slate-800 dark:text-slate-200 leading-relaxed text-sm sm:text-base min-h-[160px] max-h-[380px] overflow-y-auto pr-2 focus:outline-hidden relative"
          id="annotated-document-box-skeleton"
        >
          {rawSentences.length > 0 ? (
            <div>
              {rawSentences.map((sentence, idx) => {
                const segmentDuration = getSegmentShimmerDuration(sentence, trimmedText.length);
                return (
                  <span
                    key={idx}
                    className="inline mr-1 px-1 py-0.5 rounded skeleton-text-segment transition-all"
                    style={{
                      animationDuration: `${segmentDuration}s`,
                      ["--shimmer-duration" as any]: `${segmentDuration}s`,
                    }}
                    title={`Analyzing sentence (${sentence.trim().length} chars • ${segmentDuration}s shimmer cycle)...`}
                  >
                    {sentence}{" "}
                  </span>
                );
              })}
            </div>
          ) : (
            /* Graceful structured fallback if text has not yet been typed (longer lines have proportionally slower cycles) */
            <div className="space-y-4">
              <div className="space-y-2">
                <SkeletonBone duration={3.4} className="h-4.5 w-[96%] rounded-md" />
                <SkeletonBone duration={3.1} className="h-4.5 w-[91%] rounded-md" />
                <SkeletonBone duration={2.7} className="h-4.5 w-[85%] rounded-md" />
                <SkeletonBone duration={2.2} className="h-4.5 w-[72%] rounded-md" />
              </div>
              <div className="space-y-2">
                <SkeletonBone duration={3.3} className="h-4.5 w-[94%] rounded-md" />
                <SkeletonBone duration={2.9} className="h-4.5 w-[88%] rounded-md" />
                <SkeletonBone duration={2.0} className="h-4.5 w-[64%] rounded-md" />
              </div>
            </div>
          )}
        </div>

        {/* Bottom Scanning Telemetry Bar */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800/60 text-xs text-slate-500 font-mono">
          <span className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            <span>Scanning syntactic variance and token transition probabilities...</span>
          </span>
          {wordCount > 0 && (
            <span className="text-slate-600 dark:text-slate-400">
              {wordCount} words &bull; {sentenceCount} sentences mapped
            </span>
          )}
        </div>
      </div>

      {/* 5. Stylometric Deep Dive Diagnostics Drawer (Matches DashboardMetrics + RadarChart 1:1) */}
      <div
        className="glass-panel rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm flex flex-col gap-4 border border-slate-200/80 dark:border-slate-800/90 relative overflow-hidden"
        id="skeleton-stylometric-diagnostics"
      >
        <div className="skeleton-wave" />

        {/* Drawer Header */}
        <div className="w-full flex items-center justify-between gap-4 text-left select-none p-1 rounded-xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 shrink-0">
              <BarChart2 className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-display font-bold text-sm sm:text-base text-slate-900 dark:text-white flex items-center gap-2 flex-wrap">
                <span>Stylometric Diagnostics</span>
                <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                  7 Features + Radar
                </span>
              </h3>
              <p className="text-xs text-slate-500 truncate">
                Statistical indicators computed directly from the document's syntactic structure
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <SkeletonBone className="h-4 w-32 rounded-md hidden sm:block" />
            <div className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-400">
              <ChevronUp className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Body Container */}
        <div className="flex flex-col gap-6 pt-4 border-t border-gray-200/70 dark:border-slate-800/80">
          
          {/* Radar Chart Section (Exact 1:1 Replica of RadarChart.tsx layout) */}
          <div className="rounded-2xl border border-slate-200/80 dark:border-slate-800/80 p-4 sm:p-6 bg-slate-50/50 dark:bg-slate-900/30 relative overflow-hidden">
            <div className="skeleton-wave" />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              
              {/* Left Column: SVG Spider Web Skeleton */}
              <div className="lg:col-span-7 flex flex-col items-center justify-center relative">
                <div className="relative w-full max-w-[340px] aspect-square flex items-center justify-center">
                  <svg className="w-full h-full" viewBox="0 0 300 300">
                    {/* Concentric polygon web rings */}
                    {[0.2, 0.4, 0.6, 0.8, 1.0].map((level, lIdx) => {
                      const points = [0, 1, 2, 3, 4, 5, 6]
                        .map((i) => {
                          const angle = (Math.PI * 2 * i) / 7 - Math.PI / 2;
                          const r = 110 * level;
                          const x = 150 + r * Math.cos(angle);
                          const y = 150 + r * Math.sin(angle);
                          return `${x},${y}`;
                        })
                        .join(" ");
                      return (
                        <polygon
                          key={lIdx}
                          points={points}
                          fill="none"
                          className="stroke-slate-200 dark:stroke-slate-800"
                          strokeWidth="1.2"
                        />
                      );
                    })}

                    {/* Radial Spoke Lines */}
                    {[0, 1, 2, 3, 4, 5, 6].map((i) => {
                      const angle = (Math.PI * 2 * i) / 7 - Math.PI / 2;
                      const x2 = 150 + 110 * Math.cos(angle);
                      const y2 = 150 + 110 * Math.sin(angle);
                      return (
                        <line
                          key={i}
                          x1="150"
                          y1="150"
                          x2={x2}
                          y2={y2}
                          className="stroke-slate-200 dark:stroke-slate-800"
                          strokeWidth="1.2"
                          strokeDasharray="2 2"
                        />
                      );
                    })}

                    {/* Shimmering Sample Polygon */}
                    <polygon
                      points="150,65 210,110 220,195 160,230 100,215 85,130 115,85"
                      className="fill-slate-500/15 stroke-slate-400 dark:stroke-slate-500"
                      strokeWidth="2"
                    />
                  </svg>

                  {/* Center Scanning Badge */}
                  <div className="absolute flex flex-col items-center justify-center p-2 rounded-xl bg-white/90 dark:bg-slate-900/90 backdrop-blur-xs border border-slate-200 dark:border-slate-700 shadow-xs">
                    <RefreshCw className="w-4 h-4 text-slate-600 dark:text-slate-400 animate-spin mb-1" />
                    <span className="text-[9px] font-mono font-bold text-slate-600 dark:text-slate-400 tracking-wider">
                      COMPUTING VECTORS
                    </span>
                  </div>
                </div>
              </div>

              {/* Right Column: Radar Feature Metrics Breakdown */}
              <div className="lg:col-span-5 flex flex-col gap-3">
                <div className="space-y-1">
                  <h4 className="font-display font-bold text-sm text-slate-900 dark:text-white">
                    Stylometric Radar Chart
                  </h4>
                  <p className="text-xs text-slate-500">
                    7-Axis Feature Footprint vs. Calibrated Baselines
                  </p>
                </div>

                <div className="space-y-2 mt-2">
                  {[
                    "Sentence Length Variance (Burstiness)",
                    "Shannon Information Entropy",
                    "Vocabulary Diversity (TTR / VD)",
                    "Machine Syntactic Predictability",
                    "Zipf Power-Law Fit (R²)"
                  ].map((label, idx) => (
                    <div
                      key={idx}
                      className="p-2 rounded-xl border border-slate-200/60 dark:border-slate-800/60 bg-white/60 dark:bg-slate-800/30 flex items-center justify-between gap-3 text-xs"
                    >
                      <span className="text-slate-700 dark:text-slate-300 font-medium truncate">
                        {label}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <SkeletonBone className="h-4 w-12 rounded-md" />
                        <SkeletonBone className="h-4 w-16 rounded-md" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* Bento Grid: 6 Analytical Metric Cards (Matches DashboardMetrics 3-column layout) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" id="skeleton-metrics-grid">
            {[
              { title: "Burstiness & Variance (CV)", icon: Activity },
              { title: "Zipf's Law Goodness-of-Fit (R²)", icon: BookOpen },
              { title: "Shannon Relative Entropy (H)", icon: Cpu },
              { title: "Average Sentence Length (ASL)", icon: FileText },
              { title: "Token Repetition (Rep-Score)", icon: Repeat },
              { title: "Syntactic Predictability Score", icon: Wand2 }
            ].map((card, idx) => {
              const IconComp = card.icon;
              return (
                <div
                  key={idx}
                  className="glass-panel border border-slate-200/80 dark:border-slate-800/90 rounded-2xl p-4 sm:p-5 flex flex-col justify-between space-y-3 relative overflow-hidden shadow-xs"
                >
                  <div className="skeleton-wave" />

                  {/* Header Row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500 shrink-0">
                        <IconComp className="w-3.5 h-3.5 text-slate-500" />
                      </div>
                      <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                        {card.title}
                      </span>
                    </div>
                  </div>

                  {/* Value & Subtitle */}
                  <div>
                    <SkeletonBone className="h-7 w-24 rounded-lg my-1" />
                    <SkeletonBone className="h-3 w-36 rounded-md" />
                  </div>

                  {/* Progress Bar Track */}
                  <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 rounded-full overflow-hidden relative">
                    <div className="skeleton-wave" />
                    <SkeletonBone className="h-full w-2/3 rounded-full" />
                  </div>

                  {/* Description & Lean Pill */}
                  <div className="space-y-1.5">
                    <SkeletonBone className="h-2.5 w-full rounded-md" />
                    <SkeletonBone className="h-2.5 w-4/5 rounded-md" />
                  </div>

                  <div className="pt-2 border-t border-slate-100 dark:border-slate-800/60 flex items-center justify-between">
                    <SkeletonBone className="h-5 w-28 rounded-full" />
                    <SkeletonBone className="h-3.5 w-12 rounded-md" />
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* 7. Academic Verification Sign-off Badge (Matches App.tsx 1:1) */}
      <div className="border border-slate-200 dark:border-slate-800 rounded-2xl p-5 bg-slate-50/70 dark:bg-slate-900/40 flex flex-col md:flex-row items-center justify-between gap-4 relative overflow-hidden">
        <div className="skeleton-wave" />
        <div className="flex gap-3 items-start">
          <GraduationCap className="w-6 h-6 text-slate-700 dark:text-slate-300 shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-xs text-slate-900 dark:text-white uppercase font-mono tracking-wider">
              Academic Verification Sign-off
            </h4>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 leading-relaxed">
              Forensic report generated securely via on-device WebAssembly within the Department of Computer Engineering Technology sandbox.
            </p>
          </div>
        </div>

        <div className="border border-dashed border-slate-300 dark:border-slate-700 px-4 py-2.5 rounded-xl text-center shrink-0 bg-white dark:bg-slate-800">
          <span className="text-[9px] uppercase font-mono font-bold text-slate-500 block">Forensics Seal</span>
          <span className="text-emerald-600 dark:text-emerald-400 font-extrabold text-xs tracking-widest uppercase">
            VERIFIED SECURE
          </span>
        </div>
      </div>

      {/* 8. Next Step Flow Actions Panel (Matches App.tsx 1:1) */}
      <div
        className="border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/60 rounded-2xl sm:rounded-3xl p-4 sm:p-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 shadow-xs relative overflow-hidden"
        id="skeleton-next-actions"
      >
        <div className="skeleton-wave" />
        <div className="flex flex-col gap-1">
          <h4 className="font-display font-bold text-sm sm:text-base text-slate-900 dark:text-white">
            Next Actions
          </h4>
          <p className="text-xs text-slate-600 dark:text-slate-400">
            Continue your forensic workflow, modify your text, or run a new document analysis.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <SkeletonBone className="h-10 w-44 rounded-xl grow sm:grow-0" />
          <SkeletonBone className="h-10 w-32 rounded-xl grow sm:grow-0" />
          <SkeletonBone className="h-10 w-48 rounded-xl grow sm:grow-0" />
        </div>
      </div>
    </div>
  );
};

/**
 * OcrSkeletonLoader renders when OCR processing is in progress
 */
export const OcrSkeletonLoader: React.FC = () => {
  return (
    <div
      className="glass-panel rounded-3xl p-6 space-y-6 w-full border border-slate-200/80 dark:border-slate-800/90 relative overflow-hidden shadow-sm animate-fade-in"
      id="skeleton-ocr-loader"
    >
      <div className="skeleton-wave" />
      <div className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
        <SkeletonBone className="w-8 h-8 rounded-xl" />
        <div className="space-y-1.5 flex-1">
          <SkeletonBone className="h-4 w-48 rounded-md" />
          <SkeletonBone className="h-3 w-72 rounded-md" />
        </div>
      </div>
      <div className="h-56 bg-slate-100/70 dark:bg-slate-900/60 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-800 flex flex-col items-center justify-center gap-3 relative overflow-hidden">
        <div className="skeleton-wave" />
        <SkeletonBone className="w-12 h-12 rounded-full" />
        <SkeletonBone className="h-3.5 w-40 rounded-md" />
      </div>
    </div>
  );
};

/**
 * HistorySkeletonLoader renders placeholder items while history is loading
 */
export const HistorySkeletonLoader: React.FC = () => {
  return (
    <div
      className="glass-panel rounded-3xl p-5 space-y-3.5 w-full border border-slate-200/80 dark:border-slate-800/90 relative overflow-hidden shadow-sm animate-fade-in"
      id="skeleton-history-loader"
    >
      <div className="skeleton-wave" />
      <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
        <SkeletonBone className="h-4 w-36 rounded-md" />
        <SkeletonBone className="h-4 w-12 rounded-md" />
      </div>
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="p-3 bg-slate-100/70 dark:bg-slate-800/50 rounded-xl border border-slate-200/50 dark:border-slate-700/50 flex items-center justify-between relative overflow-hidden"
        >
          <div className="skeleton-wave" />
          <div className="space-y-1.5 flex-1 pr-4">
            <SkeletonBone className="h-3.5 w-3/4 rounded-md" />
            <SkeletonBone className="h-2.5 w-1/2 rounded-md" />
          </div>
          <SkeletonBone className="h-6 w-14 rounded-full shrink-0" />
        </div>
      ))}
    </div>
  );
};
